import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Flag, LANGS } from "@/components/common/Flags";
import { LogoHorizontal, LogoStacked } from "@/components/common/Logo";
import { BookingBar, useAnchorPassed, useIsMobile } from "@/components/booking/BookingBar";
import { useLang } from "@/lib/i18n";
import { HOTEL } from "@/lib/hotel";
import { NavPanel, PANEL_IDS } from "./NavPanels";
import { MenuOverlay } from "./MenuOverlay";
import { NAV } from "./nav";

export { NAV };

export function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = useState(() => (typeof window !== "undefined" ? window.scrollY > threshold : false));
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > threshold);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, [threshold]);
  return scrolled;
}

/**
 * Hide the bar while the reader scrolls down, bring it straight back on the first scroll up.
 * Never hidden near the top of the page.
 */
export function useHideOnScroll({ threshold = 160, delta = 8 } = {}) {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    let last = window.scrollY;
    let raf = 0;
    const on = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        const d = y - last;
        if (y < threshold) setHidden(false);
        else if (d > delta) setHidden(true);
        else if (d < -delta) setHidden(false);
        last = y;
        raf = 0;
      });
    };
    window.addEventListener("scroll", on, { passive: true });
    return () => { window.removeEventListener("scroll", on); if (raf) cancelAnimationFrame(raf); };
  }, [threshold, delta]);
  return hidden;
}

/* ------------------------------------------------------------------ language */

/**
 * Language: just the flag of the current language. Click opens a small flat menu with both flags and
 * their names. `light` flips the trigger to white over photo heroes.
 */
export const LangMenu = ({ testid = "header-language-toggle", className = "", light = false, align = "end" }) => {
  const { lang, setLang, tx } = useLang();
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`inline-flex h-10 w-10 items-center justify-center rounded-md transition-[opacity,background-color] duration-200 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:opacity-80 ${className}`}
          aria-label={`${current.label} – ${tx("byt språk", "change language")}`}
          data-testid={testid}
        >
          <Flag code={current.code} className={`h-6 w-6 ${light ? "ring-white/40" : ""}`} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} sideOffset={8} className="w-48 rounded-md border-border bg-card p-1 shadow-pop">
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onSelect={() => setLang(l.code)}
            data-testid={`${testid}-${l.code}`}
            className={`flex cursor-pointer items-center gap-3 rounded-[4px] px-2.5 py-2 text-[0.9375rem] focus:bg-secondary ${lang === l.code ? "font-semibold text-foreground" : "font-medium text-foreground/80"}`}
          >
            <Flag code={l.code} className="h-5 w-5" />
            <span className="flex-1">{l.label}</span>
            {lang === l.code && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Flat segmented switch – footer */
export const LangSwitch = ({ testid = "footer-language-toggle", light = false, className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`inline-flex items-center overflow-hidden rounded-md border ${light ? "border-white/20" : "border-border"} ${className}`} role="group" aria-label="Language" data-testid={testid}>
      {LANGS.map((l, i) => {
        const active = lang === l.code;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => setLang(l.code)}
            aria-pressed={active}
            aria-label={l.label}
            data-testid={`${testid}-${l.code}`}
            className={`inline-flex h-10 items-center justify-center gap-2 px-3 text-[0.8125rem] font-semibold transition-colors duration-200 ${i > 0 ? (light ? "border-l border-white/20" : "border-l border-border") : ""} ${
              active
                ? light ? "bg-white text-foreground" : "bg-foreground text-background"
                : light ? "text-white/75 hover:bg-white/10 hover:text-white" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`}
          >
            <Flag code={l.code} className="h-5 w-5" /> {l.short}
          </button>
        );
      })}
    </div>
  );
};

/** Compatibility alias – older call sites */
export const LangToggle = LangSwitch;

/**
 * Brand lock-up. Horizontal on desktop, stacked on phones (fits next to the right-hand cluster).
 * Colour follows `currentColor`: navy ink on light surfaces, white over photo heroes.
 */
export const Logo = ({ className = "", light = false, compact = false, variant = "auto" }) => {
  const color = light ? "text-white" : "text-primary";
  if (variant === "horizontal") return <LogoHorizontal className={`${compact ? "h-[22px]" : "h-6"} ${color} ${className}`} />;
  if (variant === "stacked") return <LogoStacked className={`${compact ? "h-8" : "h-10"} ${color} ${className}`} />;
  return (
    <span className={`inline-flex items-center ${color} ${className}`}>
      <LogoStacked className={`${compact ? "h-8" : "h-9"} lg:hidden`} />
      <LogoHorizontal className={`hidden lg:block ${compact ? "lg:h-[22px]" : "lg:h-6"}`} />
    </span>
  );
};

/* ------------------------------------------------------------------ header */

/**
 * Nav item: full-height text link with a 2px rule along the bottom edge of the bar. One shared rule
 * (layoutId) glides between items on hover and settles under the active page when the pointer leaves.
 */
const NavItem = ({ n, light, hovered, anyHover, onHover, panelOpen }) => {
  const { tx } = useLang();
  const hasPanel = PANEL_IDS.includes(n.id);
  return (
    <NavLink
      to={n.to}
      data-testid={`nav-${n.id}`}
      onMouseEnter={() => onHover(n.id)}
      onFocus={() => onHover(n.id)}
      aria-haspopup={hasPanel ? "true" : undefined}
      aria-expanded={hasPanel ? (panelOpen ? "true" : "false") : undefined}
      className={({ isActive }) =>
        `relative flex h-full items-center whitespace-nowrap px-3 text-[0.9375rem] font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring xl:px-3.5 ${
          isActive
            ? light ? "text-white" : "text-foreground"
            : light ? "text-white/80 hover:text-white" : "text-foreground/60 hover:text-foreground"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span>{tx(n.nn, n.en)}</span>
          {(hovered || (isActive && !anyHover)) && (
            <motion.span
              layoutId="header-nav-rule"
              transition={{ type: "spring", stiffness: 520, damping: 44, mass: 0.6 }}
              className={`absolute inset-x-3 -bottom-px h-[2px] xl:inset-x-3.5 ${light ? "bg-white" : "bg-foreground"}`}
              aria-hidden
            />
          )}
        </>
      )}
    </NavLink>
  );
};

/**
 * Site header – one flat, full-width bar.
 *  - over a photo hero at the top of the page: transparent, white type, a hairline along the bottom
 *  - on plain pages at the top: ivory with a bottom border
 *  - scrolled (any page): compact, ivory with a soft blur and a bottom border
 *  - scrolling down hides it, the first scroll up brings it back
 * Left: logo. Centre (desktop): the six pages – Rom / Mat / Opplev open a full-width preview panel;
 * on pages with a search the centre swaps to the compact search once the main one has scrolled away.
 * Right: phone (wide screens), language flag, «Bestill» and «Meny». The menu is the same full-screen
 * overlay on every device, so everything else (my booking, contact, Fjordverten, address) lives in
 * one predictable place instead of crowding the bar.
 */
export const Header = ({ showSearch = false, overlay = false }) => {
  const { tx } = useLang();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const scrolled = useScrolled(24);
  const isMobile = useIsMobile();
  const passed = useAnchorPassed({ offset: 72 });
  const searchVisible = showSearch && passed && !isMobile;
  const [hover, setHover] = useState(null);
  const [panel, setPanel] = useState(null);
  const onHover = (id) => { setHover(id); setPanel(PANEL_IDS.includes(id) ? id : null); };
  const closePanels = () => { setHover(null); setPanel(null); };
  useEffect(() => { setOpen(false); closePanels(); }, [pathname]);
  useEffect(() => {
    if (!panel) return undefined;
    const onKey = (e) => { if (e.key === "Escape") closePanels(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panel]);
  const panelVisible = Boolean(panel) && !searchVisible && !isMobile;
  const light = overlay && !scrolled && !panelVisible;
  const solid = !light;
  const hiddenByScroll = useHideOnScroll();
  const hidden = hiddenByScroll && !panelVisible && !open;

  const ctl = scrolled ? "h-9" : "h-10";

  return (
    <header
      className={`no-print fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,backdrop-filter,transform] duration-300 ease-premium ${hidden ? "-translate-y-full" : "translate-y-0"} ${
        solid
          ? `border-border ${panelVisible ? "bg-background" : "bg-background/90 backdrop-blur-xl supports-[backdrop-filter]:bg-background/85"}`
          : "border-white/[0.14] bg-transparent"
      }`}
      data-testid="site-header"
      data-hidden={hidden ? "true" : "false"}
      data-state={searchVisible ? "search" : "nav"}
      data-light={light ? "true" : "false"}
      data-scrolled={scrolled ? "true" : "false"}
      data-panel={panelVisible ? panel : "none"}
      onMouseLeave={closePanels}
    >
      <div className={`mx-auto flex w-full max-w-site items-stretch justify-between gap-5 px-5 transition-[height] duration-300 ease-premium sm:px-8 lg:gap-8 lg:px-10 ${scrolled ? "h-14 lg:h-16" : "h-16 lg:h-20"}`}>
        <Link to="/" onMouseEnter={() => setPanel(null)} className="flex shrink-0 items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="header-logo-link" aria-label={`${HOTEL.name} – ${tx("heim", "home")}`}>
          <Logo light={light} compact={scrolled} />
        </Link>

        <div className="relative hidden min-w-0 flex-1 lg:flex lg:items-stretch lg:justify-center">
          <AnimatePresence initial={false} mode="wait">
            {searchVisible ? (
              <motion.div
                key="search"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
                className="flex items-center justify-center"
                data-testid="sticky-booking-bar"
              >
                <BookingBar variant="compact" testid="sticky-booking-bar-inner" />
              </motion.div>
            ) : (
              <motion.nav
                key="nav"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
                className="flex items-stretch"
                aria-label={tx("Hovudmeny", "Main menu")}
                data-testid="header-primary-nav"
                onMouseLeave={() => setHover(null)}
              >
                {NAV.map((n) => (
                  <NavItem key={n.to} n={n} light={light} hovered={hover === n.id} anyHover={Boolean(hover)} onHover={onHover} panelOpen={panelVisible && panel === n.id} />
                ))}
              </motion.nav>
            )}
          </AnimatePresence>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 sm:gap-3 lg:gap-4" onMouseEnter={() => setPanel(null)}>
          <a
            href={HOTEL.tel}
            className={`hidden items-center gap-2 whitespace-nowrap text-[0.9375rem] font-medium tabular transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring xl:inline-flex ${light ? "text-white/85 hover:text-white" : "text-foreground/70 hover:text-foreground"}`}
            data-testid="header-phone-link"
            aria-label={`${tx("Ring oss", "Call us")} ${HOTEL.phoneDisplay}`}
          >
            <Phone className="h-4 w-4" strokeWidth={1.75} /> {HOTEL.phoneDisplay}
          </a>
          <LangMenu light={light} className={light ? "hover:bg-white/10" : "hover:bg-secondary"} />
          <Button asChild variant={light ? "inverse" : "default"} className={`${ctl} rounded-md px-4 transition-[height,padding,background-color,color] duration-300 sm:px-5`} data-testid="header-book-cta">
            <Link to="/bestill">{tx("Bestill", "Book")}</Link>
          </Button>
          <button
            type="button"
            className={`inline-flex ${ctl} items-center gap-2 rounded-md border px-3 text-[0.9375rem] font-semibold transition-[height,border-color,background-color,color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              light ? "border-white/35 text-white hover:border-white/70 hover:bg-white/10" : "border-border text-foreground hover:border-foreground/40 hover:bg-secondary"
            }`}
            onClick={() => setOpen(true)}
            aria-label={tx("Opne meny", "Open menu")}
            aria-haspopup="dialog"
            aria-expanded={open ? "true" : "false"}
            data-testid="header-mobile-menu-button"
          >
            <span className="hidden min-[440px]:inline lg:hidden xl:inline">{tx("Meny", "Menu")}</span>
            <Menu className="h-[20px] w-[20px]" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {/* Preview panel (desktop) – full width, flush under the bar; the page beneath dims slightly */}
      <AnimatePresence>
        {panelVisible && (
          <motion.div
            key="dim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="pointer-events-none absolute inset-x-0 top-full -z-10 hidden h-screen bg-[hsl(214_40%_8%_/_0.35)] lg:block"
            aria-hidden
          />
        )}
        {panelVisible && (
          <motion.div
            key={panel}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            className="absolute inset-x-0 top-full hidden border-b border-border bg-background shadow-pop lg:block"
            data-testid="header-nav-panel"
          >
            <div className="mx-auto w-full max-w-site px-5 py-8 sm:px-8 lg:px-10">
              <NavPanel id={panel} onNavigate={closePanels} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <MenuOverlay open={open} onOpenChange={setOpen} />
    </header>
  );
};
